import dayjs from "dayjs";

/**
 * 轮换服务 - 处理航班轮换相关逻辑
 */
class RotationService {
    /**
     * 重新计算所有航班的轮换信息
     * @param {Array} flights - 航班数组
     * @param {Set} transportOrigins - 运输需求起始点集合
     * @returns {Array} 更新后的航班数组
     */
    static recalculateRotations(flights, transportOrigins) {
        if (!flights || flights.length === 0) return flights;

        try {
            // 创建一个新数组来存储更新后的航班，避免直接修改状态
            let newFlightsData = flights.map((f) => ({
                ...f,
                rotation_no: null,
                rotation_leg_index: null,
                rotation_index: null,
                is_rotation_complete: null,
            }));

            // 按飞机注册号分组
            const flightsByAircraft = newFlightsData.reduce((acc, flight) => {
                if (flight.aircraft_reg) {
                    if (!acc[flight.aircraft_reg]) {
                        acc[flight.aircraft_reg] = [];
                    }
                    acc[flight.aircraft_reg].push(flight);
                }
                return acc;
            }, {});

            let globalRotationIndex = 1;
            // 修复：不再使用当前日期，而是在 processRotationCompletion 中使用首航班的日期
            // const dateStr = dayjs().format("YYYYMMDD"); // 已移除

            // 遍历每架飞机
            Object.keys(flightsByAircraft).forEach((reg) => {
                const aircraftFlights = flightsByAircraft[reg];
                // 按起飞时间排序
                aircraftFlights.sort((a, b) => dayjs(a.std).valueOf() - dayjs(b.std).valueOf());

                let currentRotationFlights = [];
                let currentRotationIndex = -1; // 当前轮换的全局索引

                for (let i = 0; i < aircraftFlights.length; i++) {
                    const flight = aircraftFlights[i];

                    if (currentRotationFlights.length === 0) {
                        // 开始一个新的轮换
                        currentRotationIndex = globalRotationIndex++;
                        currentRotationFlights.push(flight);
                        flight.rotation_index = currentRotationIndex;
                        flight.rotation_leg_index = 0;
                    } else {
                        // 检查是否需要开始新的轮换
                        const prevFlight = currentRotationFlights[currentRotationFlights.length - 1];
                        const isAirportDiscontinuity = prevFlight.arrival_airport !== flight.departure_airport;
                        const isTimeDiscontinuity = dayjs(flight.std).isBefore(dayjs(prevFlight.sta));
                        const isFromTransportOrigin = transportOrigins.has(flight.departure_airport);

                        // 满足以下条件之一时，开始新的轮换：
                        // 1. 航线不连续 且 当前航班从运输需求始发地出发
                        // 2. 时间不连续（当前航班起飞时间早于前一航班到达时间）
                        if ((isAirportDiscontinuity && isFromTransportOrigin) || isTimeDiscontinuity) {
                            // 结束上一个轮换
                            this.processRotationCompletion(currentRotationFlights, transportOrigins);

                            // 开始新的轮换
                            currentRotationIndex = globalRotationIndex++;
                            currentRotationFlights = [flight]; // 新的轮换从当前航班开始
                            flight.rotation_index = currentRotationIndex;
                            flight.rotation_leg_index = 0;
                        } else {
                            // 否则继续添加到当前轮换
                            flight.rotation_index = currentRotationIndex;
                            flight.rotation_leg_index = currentRotationFlights.length;
                            currentRotationFlights.push(flight);
                        }
                    }

                    // 检查当前航班是否结束了一个轮换（到达运输需求始发地）
                    if (transportOrigins.has(flight.arrival_airport)) {
                        // 在始发地落地，结束当前轮换
                        this.processRotationCompletion(currentRotationFlights, transportOrigins);
                        currentRotationFlights = []; // 清空，下一个航班将开始新的轮换
                    }
                }
                // 处理最后一组未结束的轮换
                if (currentRotationFlights.length > 0) {
                    this.processRotationCompletion(currentRotationFlights, transportOrigins);
                }
            });

            return newFlightsData;
        } catch (error) {
            console.error("计算轮换失败:", error);
            return flights; // 失败时返回原始航班
        }
    }

    /**
     * 处理轮换完成状态和编号分配
     * @param {Array} rotationFlights - 轮换中的航班
     * @param {Set} transportOrigins - 运输需求起始点集合
     */
    static processRotationCompletion(rotationFlights, transportOrigins) {
        if (!rotationFlights || rotationFlights.length === 0) return;

        const firstFlight = rotationFlights[0];
        const lastFlight = rotationFlights[rotationFlights.length - 1];
        const rotationIndex = firstFlight.rotation_index;

        // 修复：使用首航班的起飞日期作为轮换编号的日期部分
        // 这样确保同样的数据无论何时计算，结果都是一致的
        const dateStr = dayjs(firstFlight.std).format("YYYYMMDD");

        // 简化逻辑：
        // 1. 首航班从运输需求始发地起飞
        // 2. 尾航班在运输需求始发地落地
        // 满足这两个条件就是完整轮换，否则是不完整轮换
        const startsAtOrigin = transportOrigins.has(firstFlight.departure_airport);
        const isSingleFlight = rotationFlights.length === 1;
        const endsAtOrigin = transportOrigins.has(lastFlight.arrival_airport);

        // 只有两个条件都满足才是完整轮换
        const isComplete = startsAtOrigin && endsAtOrigin && !isSingleFlight;

        // 根据完整性决定轮换编号前缀
        const finalRotationNo = `${isComplete ? "ROT" : "PART"}-${dateStr}-${rotationIndex}`;

        // 为轮换中的所有航班分配最终编号和完成状态
        rotationFlights.forEach((flight) => {
            flight.rotation_no = finalRotationNo;
            flight.is_rotation_complete = isComplete;
        });
    }

    /**
     * 从航班数据中提取运输需求起点集合
     * @param {Array} flights - 航班数组
     * @param {Array} transportationDemands - 运输需求数组
     * @returns {Set} 运输需求起点集合
     */
    static getTransportOrigins(flights, transportationDemands) {
        const transportOrigins = new Set();

        // 从运输需求中提取始发地
        if (transportationDemands && transportationDemands.length > 0) {
            transportationDemands.forEach((demand) => {
                if (demand.origin) {
                    transportOrigins.add(demand.origin);
                }
            });
        }

        // 如果没有定义运输需求，则使用所有机场作为可能的起点
        if (transportOrigins.size === 0 && flights && flights.length > 0) {
            const allAirports = new Set();
            flights.forEach((flight) => {
                allAirports.add(flight.departure_airport);
                allAirports.add(flight.arrival_airport);
            });
            allAirports.forEach((airport) => transportOrigins.add(airport));
        }

        return transportOrigins;
    }
}

export default RotationService;
